/**
 * TasksByPersonView Component
 * Displays tasks grouped by assignee using UserTaskSummaryRow
 */

import { useMemo } from "react";
import { VStack } from "@chakra-ui/react";
import { BasicUser, User } from "features/User/models";
import { RelationType } from "models/v2/onboardingJourneyNetworkRelation";
import { Task } from "features/Resources/Task/models";
import { isTaskOpen } from "features/Resources/Task/helpers";
import UserTaskSummaryRow from "./UserTaskSummaryRow";

interface StakeholderWithTasks {
  user: User | BasicUser;
  roles: RelationType[];
  tasks: Task[];
}

interface RelationalPerson {
  user: User | BasicUser | undefined;
  roles: RelationType[];
}

interface TasksByPersonViewProps {
  tasks: Task[];
  currentUserId?: string;
  includeNotStarted?: boolean;
  hideClosed?: boolean;
  expandedUserId?: string;
  compact?: boolean;
  /** Relational people to always include (even if they have no tasks) */
  relationalPeople?: RelationalPerson[];
  onRemind?: (user: User | BasicUser, tasks: Task[]) => void;
  resolveHref?: string;
  onTaskClick?: (task: Task) => void;
}

export default function TasksByPersonView({
  tasks,
  currentUserId,
  includeNotStarted = true,
  hideClosed = false,
  expandedUserId,
  compact = false,
  relationalPeople = [],
  onRemind,
  resolveHref,
  onTaskClick,
}: TasksByPersonViewProps) {
  // Group tasks by assignee, ensuring relational people are always included
  const stakeholders = useMemo(() => {
    const userMap = new Map<string, StakeholderWithTasks>();

    // First, add all relational people (they'll be included even without tasks)
    relationalPeople.forEach(({ user, roles }) => {
      if (!user) return;
      userMap.set(user.id, {
        user,
        roles,
        tasks: [],
      });
    });

    // Then, add tasks to their assignees (creating new entries for non-relational assignees)
    tasks.forEach((task) => {
      if (!task.assignee) return;

      const userId = task.assignee.id;
      let entry = userMap.get(userId);
      if (!entry) {
        entry = {
          user: task.assignee,
          roles: task.assigneeRole ? [task.assigneeRole] : [],
          tasks: [],
        };
        userMap.set(userId, entry);
      }
      entry.tasks.push(task);
    });

    // Sort: current user first, then by number of open tasks (descending)
    const sortedStakeholders = Array.from(userMap.values()).sort((a, b) => {
      // Current user first
      if (a.user.id === currentUserId) return -1;
      if (b.user.id === currentUserId) return 1;

      // Then by open task count
      const aOpenCount = a.tasks.filter(isTaskOpen).length;
      const bOpenCount = b.tasks.filter(isTaskOpen).length;
      return bOpenCount - aOpenCount;
    });

    return sortedStakeholders;
  }, [tasks, currentUserId, relationalPeople]);

  return (
    <VStack spacing={2} align="stretch" w="100%">
      {stakeholders.map((stakeholder) => (
        <UserTaskSummaryRow
          key={stakeholder.user.id}
          user={stakeholder.user}
          roles={stakeholder.roles}
          tasks={stakeholder.tasks}
          includeNotStarted={includeNotStarted}
          hideClosed={hideClosed}
          isCurrentUser={stakeholder.user.id === currentUserId}
          defaultIsOpen={
            expandedUserId !== undefined
              ? stakeholder.user.id === expandedUserId
              : stakeholder.tasks.filter(isTaskOpen).length > 0
          }
          compact={compact}
          onRemind={onRemind ? () => onRemind(stakeholder.user, stakeholder.tasks) : undefined}
          resolveHref={stakeholder.user.id === currentUserId ? resolveHref : undefined}
          onTaskClick={onTaskClick}
        />
      ))}
    </VStack>
  );
}
